import { useState } from "react";
import api, { useList, errMsg, fmtRM } from "@/lib/api";
import ResourcePage from "@/components/ResourcePage";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ListOrdered, Plus, X, AlertTriangle } from "lucide-react";

// Inline toggle so admin can flip a single sub-course visibility without opening Edit
function PublicVisibilityToggle({ course, onChanged }) {
  const [busy, setBusy] = useState(false);
  const flip = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.put(`/courses/${course.id}`, {
        public_visible: !course.public_visible,
      });
      toast.success(course.public_visible ? "Hidden from public site" : "Showing on public site");
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Switch checked={!!course.public_visible} disabled={busy} onCheckedChange={() => flip({ stopPropagation: () => {} })}
              data-testid={`course-visible-${course.id}`} />
      <span className="text-xs text-muted-foreground">{course.public_visible ? "Visible" : "Hidden"}</span>
    </div>
  );
}

export function CoursesPage() {
  const [levelsCourse, setLevelsCourse] = useState(null);
  const [newLevel, setNewLevel] = useState("");
  const { data: courses, reload } = useList("/courses");

  const openLevels = (row) => setLevelsCourse(row);
  const current = levelsCourse && (courses || []).find((c) => c.id === levelsCourse.id);

  const addLevel = async () => {
    if (!newLevel.trim()) return;
    try {
      await api.post(`/courses/${levelsCourse.id}/levels`, { level_name: newLevel.trim() });
      setNewLevel("");
      reload();
      toast.success("Level added");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeLevel = async (levelId) => {
    try {
      await api.delete(`/courses/${levelsCourse.id}/levels/${levelId}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <ResourcePage
        testId="courses-page"
        title="Courses"
        description='Instruments and programmes. Group sub-courses like "Yamaha Piano Course 1-12" under a Master series name so the public website shows one master tile.'
        endpoint="/courses"
        canDelete={true}
        createLabel="Add course"
        searchKeys={["name", "category", "master_course_name"]}
        columns={[
          { key: "name", label: "Course", render: (r) => (
            <div>
              <p className="font-medium">{r.name}</p>
              {r.master_course_name && (
                <p className="text-[10px] text-muted-foreground">↳ part of <b>{r.master_course_name}</b>{r.level_within_master ? ` · level ${r.level_within_master}` : ""}</p>
              )}
            </div>
          ) },
          { key: "category", label: "Category" },
          { key: "public", label: "Public site", render: (r) => (
            <PublicVisibilityToggle course={r} onChanged={reload} />
          ) },
          { key: "levels", label: "Grades", render: (r) => (
            <Button variant="outline" size="sm" data-testid={`levels-button-${r.id}`} onClick={() => openLevels(r)}>
              <ListOrdered className="h-3.5 w-3.5 mr-1" /> {(r.levels || []).length} grades
            </Button>
          )},
          { key: "status", label: "Status", badge: true },
        ]}
        fields={[
          { name: "name", label: "Internal course name", required: true,
            placeholder: 'Used by admin & invoices (e.g. "Yamaha Piano Course 1")' },
          { name: "public_display_name", label: "Public display name (optional)",
            placeholder: 'Simplified name shown on public site (e.g. "Classical Piano")' },
          { name: "category", label: "Category", placeholder: "e.g. Keyboard, Strings, Voice" },
          { name: "master_course_name", label: "Master series (optional)",
            placeholder: 'e.g. "Yamaha Piano Course" — groups levels into one master tile' },
          { name: "level_within_master", label: "Level # within master", type: "number",
            placeholder: "e.g. 1, 2, 3 — controls sort order" },
          { name: "public_visible", label: "Show on public website", type: "switch", defaultValue: false },
          { name: "status", label: "Status", type: "select", defaultValue: "active", options: [
            { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
          { name: "description", label: "Description", type: "textarea" },
        ]}
      />
      <Dialog open={!!levelsCourse} onOpenChange={(o) => !o && setLevelsCourse(null)}>
        <DialogContent data-testid="levels-dialog">
          <DialogHeader><DialogTitle className="font-display">Levels — {levelsCourse?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(current?.levels || []).map((l) => (
              <div key={l.id} className="flex justify-between items-center text-sm border rounded-md px-3 py-2">
                <span>{l.sequence_order}. {l.level_name}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLevel(l.id)} data-testid={`remove-level-${l.id}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Input data-testid="new-level-input" placeholder="e.g. Grade 3" value={newLevel} onChange={(e) => setNewLevel(e.target.value)} />
              <Button onClick={addLevel} data-testid="add-level-button"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CourseFeesPage() {
  const { data: courses } = useList("/courses");
  return (
    <ResourcePage
      testId="course-fees-page"
      title="Course Fees"
      description="Monthly music course fees per course (and optionally per level). New fees supersede old ones from their effective date — past invoices are never changed."
      endpoint="/course-fees"
      createLabel="Set course fee"
      searchKeys={["course_name", "level_name"]}
      columns={[
        { key: "course_name", label: "Course", render: (r) => <span className="font-medium">{r.course_name}</span> },
        { key: "level_name", label: "Level", render: (r) => r.level_name || "All levels" },
        { key: "music_course_fee", label: "Monthly fee", render: (r) => fmtRM(r.music_course_fee) },
        { key: "registration_fee", label: "Registration", render: (r) => fmtRM(r.registration_fee) },
        { key: "trial_fee", label: "Trial", render: (r) => fmtRM(r.trial_fee) },
        { key: "effective_start_date", label: "Effective from" },
        { key: "status", label: "Status", badge: true },
      ]}
      canEdit={false}
      fields={[
        { name: "course_id", label: "Course", type: "select", required: true,
          options: (courses || []).map((c) => ({ value: c.id, label: c.name })) },
        { name: "level_id", label: "Level (optional)", type: "select",
          options: (values) => {
            const c = (courses || []).find((x) => x.id === values.course_id);
            return (c?.levels || []).map((l) => ({ value: l.id, label: l.level_name }));
          } },
        { name: "music_course_fee", label: "Monthly course fee (RM)", type: "number", step: "0.01", required: true },
        { name: "registration_fee", label: "Registration fee (RM)", type: "number", step: "0.01", defaultValue: "72" },
        { name: "material_fee", label: "Material fee (RM)", type: "number", step: "0.01", defaultValue: "30" },
        { name: "trial_fee", label: "Trial class fee (RM)", type: "number", step: "0.01", defaultValue: "30" },
        { name: "effective_start_date", label: "Effective from", type: "date" },
      ]}
      transform={(p) => ({ ...p, status: "active" })}
    />
  );
}

export function StudiosPage() {
  return (
    <ResourcePage
      testId="studios-page"
      title="Studios"
      description="Teaching rooms available for classes and bookings."
      endpoint="/studios"
      createLabel="Add studio"
      searchKeys={["name", "equipment"]}
      columns={[
        { key: "name", label: "Studio", render: (r) => <span className="font-medium">{r.name}</span> },
        { key: "capacity", label: "Capacity" },
        { key: "equipment", label: "Equipment" },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={[
        { name: "name", label: "Studio name", required: true },
        { name: "capacity", label: "Capacity", type: "number", int: true, defaultValue: "1" },
        { name: "equipment", label: "Equipment", type: "textarea" },
        { name: "status", label: "Status", type: "select", defaultValue: "active", options: [
          { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
      ]}
    />
  );
}

export function FeeSettingsPage() {
  const { data: settings, reload } = useList("/fee-settings");
  const { data: active, reload: reloadActive } = useList("/fee-settings/active");
  const [adminFee, setAdminFee] = useState("");
  const [sstPct, setSstPct] = useState("");
  const [effDate, setEffDate] = useState("");
  const [calcFee, setCalcFee] = useState("140");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!adminFee || !sstPct) { toast.error("Admin fee and SST rate are required"); return; }
    setBusy(true);
    try {
      await api.post("/fee-settings", {
        default_school_admin_fee: parseFloat(adminFee),
        default_sst_rate: parseFloat(sstPct) / 100,
        effective_start_date: effDate || undefined,
      });
      toast.success("New fee settings saved — old invoices keep their historical rates");
      setAdminFee(""); setSstPct(""); setEffDate("");
      reload(); reloadActive();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post("/fee-settings/preview", {
        music_course_fee: parseFloat(calcFee || 0),
        school_admin_fee: adminFee ? parseFloat(adminFee) : null,
        sst_rate: sstPct ? parseFloat(sstPct) / 100 : null,
      });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!active) return <Spinner full />;
  return (
    <div data-testid="fee-settings-page">
      <PageHeader title="Fee & SST Settings"
        description="School Admin Fee and SST rate applied to all invoices. Changes take effect from their effective date; historical invoices are preserved." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-lg border bg-primary text-primary-foreground p-6" data-testid="active-fee-card">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground/60 mb-4">Currently active</p>
          <p className="text-sm text-primary-foreground/70">School Admin Fee</p>
          <p className="text-3xl font-bold font-display">{fmtRM(active.default_school_admin_fee)}</p>
          <p className="text-sm text-primary-foreground/70 mt-4">SST Rate</p>
          <p className="text-3xl font-bold font-display">{Math.round(active.default_sst_rate * 10000) / 100}%</p>
          {active.effective_start_date && (
            <p className="text-xs text-primary-foreground/60 mt-4">Effective since {active.effective_start_date}</p>
          )}
        </div>

        <div className="rounded-lg border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">New fee setting</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">School Admin Fee (RM)</Label>
              <Input data-testid="admin-fee-input" type="number" step="0.01" placeholder="55.00" value={adminFee} onChange={(e) => setAdminFee(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">SST Rate (%)</Label>
              <Input data-testid="sst-rate-input" type="number" step="0.1" placeholder="8" value={sstPct} onChange={(e) => setSstPct(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Effective from</Label>
              <Input data-testid="effective-date-input" type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} />
            </div>
            <Button onClick={save} disabled={busy} className="w-full" data-testid="save-fee-settings-button">
              {busy ? "Saving..." : "Save new settings"}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">SST calculator</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Music course fee (RM)</Label>
              <Input data-testid="calc-fee-input" type="number" step="0.01" value={calcFee} onChange={(e) => setCalcFee(e.target.value)} />
            </div>
            <Button variant="outline" onClick={runPreview} className="w-full" data-testid="calc-preview-button">Calculate</Button>
            {preview && (
              <div className="text-sm space-y-1.5 font-mono bg-muted rounded-md p-4" data-testid="calc-result">
                <p>Course fee: {fmtRM(preview.music_course_fee)}</p>
                <p>Admin fee: {fmtRM(preview.school_admin_fee)}</p>
                <p>Subtotal: {fmtRM(preview.subtotal_before_sst)}</p>
                <p>SST ({Math.round(preview.sst_rate * 10000) / 100}%): {fmtRM(preview.sst_amount)}</p>
                <p className="font-bold border-t pt-1.5">Total: {fmtRM(preview.total_fees_collected)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-3">Settings history</h3>
        <div className="rounded-lg border bg-card overflow-x-auto">
          {!settings || settings.length === 0 ? <div className="p-6"><EmptyState /></div> : (
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Admin fee</TableHead><TableHead>SST</TableHead>
                <TableHead>Effective from</TableHead><TableHead>Effective to</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {settings.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{fmtRM(s.default_school_admin_fee)}</TableCell>
                    <TableCell>{Math.round(s.default_sst_rate * 10000) / 100}%</TableCell>
                    <TableCell>{s.effective_start_date}</TableCell>
                    <TableCell>{s.effective_end_date || "—"}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <DangerZoneCard />
      <BillplzGatewayCard />
    </div>
  );
}


// ---- Danger zone: one-click reset of all transactional QA data ----
function DangerZoneCard() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const openDialog = async () => {
    setResult(null); setConfirmText("");
    try {
      const { data } = await api.get("/admin/settings/reset-test-data/preview");
      setPreview(data);
      setOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const runReset = async () => {
    if (confirmText.trim().toUpperCase() !== "RESET") return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/settings/reset-test-data",
                                      { confirmation: "RESET" });
      setResult(data);
      toast.success(`Cleared ${data.total_deleted} records — snapshot saved`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 rounded-lg border-2 border-destructive/30 bg-destructive/5 p-6"
      data-testid="danger-zone-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-destructive/15 flex items-center justify-center text-destructive">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-destructive">Danger zone</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Reset all transactional QA data — leaves, reschedules, replacements, upgrades,
            drop-outs, invoices, payments and payroll — so testing can restart on a clean slate.
            Users, teachers, parents, students, courses, class schedules, studios, and school
            settings are <span className="font-semibold">untouched</span>. A full JSON snapshot is
            written to <span className="font-mono text-xs">/app/backend/scripts/</span> before
            deletion so ops can restore.
          </p>
          <Button variant="destructive" onClick={openDialog} className="mt-4"
            data-testid="reset-test-data-open-btn">
            🧹 Reset test data
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent className="max-w-lg" data-testid="reset-test-data-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {result ? "Reset complete" : "Reset transactional data"}
            </DialogTitle>
            <DialogDescription>
              {result
                ? "The wipe finished. A JSON snapshot was written so ops can restore if needed."
                : "This deletes all transactional records (leaves, reschedules, invoices, payments, etc.) and writes a snapshot first. Users, teachers, parents, students, courses, class schedules and studios are untouched."}
            </DialogDescription>
          </DialogHeader>
          {!result && preview && (
            <div className="space-y-4">
              {preview.reset_disabled && (
                <div className="rounded-md bg-destructive/10 border border-destructive p-3 text-xs text-destructive">
                  This environment has <code>ALLOW_TEST_RESET=0</code>. Reset is disabled.
                </div>
              )}
              <p className="text-sm">
                This will delete <span className="font-bold text-destructive">
                  {preview.total} record(s)</span> across {preview.counts.length} collections.
                A snapshot is written first so an ops engineer can restore.
              </p>
              <div className="max-h-56 overflow-y-auto text-xs font-mono bg-muted rounded-md p-3 space-y-0.5"
                data-testid="reset-preview-counts">
                {preview.counts.filter((c) => c.count > 0).map((c) => (
                  <div key={c.label} className="flex justify-between">
                    <span>{c.label}</span>
                    <span className="font-bold">{c.count}</span>
                  </div>
                ))}
                {preview.counts.every((c) => c.count === 0) && (
                  <p className="text-muted-foreground">All collections are already empty.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Type <span className="text-destructive font-mono">RESET</span> to confirm
                </Label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESET" data-testid="reset-confirm-input" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                  data-testid="reset-cancel-btn">Cancel</Button>
                <Button variant="destructive"
                  disabled={preview.reset_disabled || busy || confirmText.trim().toUpperCase() !== "RESET"}
                  onClick={runReset}
                  data-testid="reset-confirm-btn">
                  {busy ? "Resetting…" : `Delete ${preview.total} records`}
                </Button>
              </div>
            </div>
          )}
          {result && (
            <div className="space-y-3 text-sm" data-testid="reset-result">
              <p>Cleared <span className="font-bold">{result.total_deleted}</span> records.</p>
              <div className="rounded-md bg-muted p-3 text-xs font-mono break-all">
                Snapshot: {result.snapshot_path} ({result.snapshot_size_kb} KB)
              </div>
              <div className="max-h-40 overflow-y-auto text-xs font-mono bg-muted rounded-md p-3 space-y-0.5">
                {Object.entries(result.deleted_by_collection)
                  .filter(([, n]) => n > 0)
                  .map(([label, n]) => (
                    <div key={label} className="flex justify-between">
                      <span>{label}</span><span className="font-bold">{n}</span>
                    </div>
                  ))}
              </div>
              <Button className="w-full" onClick={() => setOpen(false)}
                data-testid="reset-close-btn">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---- Billplz Payment Gateway (Super Admin) ----
function BillplzGatewayCard() {
  const { data: settings, reload } = useList("/payments/billplz/settings");
  const [testOpen, setTestOpen] = useState(false);
  const [testAmount, setTestAmount] = useState("1.00");
  const [testEmail, setTestEmail] = useState("yamaha.atria@gmail.com");
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const loadLog = async () => {
    try {
      const { data } = await api.get("/payments/billplz/attempts?limit=50");
      setAttempts(data.items || []);
      setShowLog(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/billplz/test-bill", {
        amount_rm: parseFloat(testAmount) || 1.00,
        email: testEmail,
        name: "Cornerstone Sandbox Tester",
      });
      toast.success(`Test bill created — opening Billplz…`);
      window.open(data.payment_url, "_blank");
      setTestOpen(false);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!settings) return null;
  const configured = settings.configured;
  const sandbox = settings.mode === "sandbox";

  return (
    <div className="mt-10 rounded-lg border-2 border-[#2D6A4F]/30 bg-[#F0F9F4] p-6"
         data-testid="billplz-gateway-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-[#2D6A4F]/15 flex items-center justify-center text-[#2D6A4F]">
          💳
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-[#2D6A4F]">Billplz Payment Gateway (Malaysia)</h3>
            {sandbox && (
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300"
                    data-testid="billplz-sandbox-badge">
                SANDBOX — TEST MODE
              </span>
            )}
            {configured
              ? <StatusBadge status="active" />
              : <StatusBadge status="pending" />}
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            FPX bank-transfer payments for Malaysian parents. Runs in SANDBOX until Super Admin swaps in production credentials.
            Stripe stays available as a separate gateway — the two never mix.
          </p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <div className={`rounded border px-3 py-2 ${settings.has_secret_key ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}>
              <span className="font-mono">BILLPLZ_SECRET_KEY</span>: {settings.has_secret_key ? "✅ set" : "❌ missing"}
            </div>
            <div className={`rounded border px-3 py-2 ${settings.has_collection_id ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}>
              <span className="font-mono">BILLPLZ_COLLECTION_ID</span>: {settings.has_collection_id ? "✅ set" : "❌ missing"}
            </div>
            <div className={`rounded border px-3 py-2 ${settings.has_x_signature_key ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}>
              <span className="font-mono">BILLPLZ_X_SIGNATURE_KEY</span>: {settings.has_x_signature_key ? "✅ set" : "❌ missing"}
            </div>
          </div>

          <div className="mt-3 rounded bg-white/60 border px-3 py-2 text-xs">
            <div><span className="text-muted-foreground">API base:</span> <span className="font-mono">{settings.api_base_url}</span></div>
            <div><span className="text-muted-foreground">Callback URL:</span> <span className="font-mono break-all">{settings.callback_url}</span></div>
            {settings.last_callback && (
              <div className="mt-1 text-muted-foreground">
                Last callback: {new Date(settings.last_callback.received_at).toLocaleString()}
                {" · verified=" + String(settings.last_callback.verified)}
                {settings.last_callback.verify_reason ? ` (${settings.last_callback.verify_reason})` : ""}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={reload} data-testid="billplz-refresh-btn">
              Refresh status
            </Button>
            <Button size="sm" disabled={!configured || !sandbox}
                    onClick={() => setTestOpen(true)} data-testid="billplz-open-test-btn"
                    className="bg-[#2D6A4F] hover:bg-[#245C42] text-white">
              Send sandbox test payment
            </Button>
            <Button variant="outline" size="sm" onClick={loadLog} data-testid="billplz-view-log-btn">
              View payment log
            </Button>
          </div>

          {!configured && (
            <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Set <code>BILLPLZ_SECRET_KEY</code>, <code>BILLPLZ_COLLECTION_ID</code> and <code>BILLPLZ_X_SIGNATURE_KEY</code> in the backend <code>.env</code> file (never in frontend or GitHub), then restart the backend.
            </p>
          )}
        </div>
      </div>

      {/* Test bill dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="billplz-test-dialog">
          <DialogHeader>
            <DialogTitle>Send sandbox test payment</DialogTitle>
            <DialogDescription>
              Creates a real bill in Billplz Sandbox. You&apos;ll be redirected to complete the fake payment. Uses sandbox test banks — no real money moves.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Amount (RM)</label>
              <input type="number" step="0.01" min="1" className="w-full mt-1 rounded border px-3 py-2 text-sm"
                     value={testAmount} onChange={(e) => setTestAmount(e.target.value)}
                     data-testid="billplz-test-amount-input" />
            </div>
            <div>
              <label className="text-xs font-medium">Payer email</label>
              <input type="email" className="w-full mt-1 rounded border px-3 py-2 text-sm"
                     value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                     data-testid="billplz-test-email-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={sendTest} disabled={busy} data-testid="billplz-test-send-btn">
              {busy ? "Creating bill…" : "Create + open Billplz"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment log */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="max-w-3xl" data-testid="billplz-log-dialog">
          <DialogHeader>
            <DialogTitle>Billplz Payment Log</DialogTitle>
            <DialogDescription>
              Latest 50 payment attempts. Callback verification and status tracked per attempt.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[500px] overflow-y-auto">
            {attempts.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">No attempts yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background border-b">
                  <tr><th className="text-left p-2">When</th><th className="text-left p-2">Invoice</th><th className="text-left p-2">Amount</th><th className="text-left p-2">Env</th><th className="text-left p-2">Status</th><th className="text-left p-2">Bill ID</th></tr>
                </thead>
                <tbody>
                  {attempts.map((a) => (
                    <tr key={a.id} className="border-b" data-testid={`billplz-log-row-${a.id}`}>
                      <td className="p-2">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="p-2 font-mono">{a.invoice_number}</td>
                      <td className="p-2">RM {(a.amount_sen / 100).toFixed(2)}</td>
                      <td className="p-2">{a.environment}</td>
                      <td className="p-2"><StatusBadge status={a.status} /></td>
                      <td className="p-2 font-mono text-[10px] break-all">{a.bill_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex justify-end mt-3">
            <Button onClick={() => setShowLog(false)} data-testid="billplz-log-close-btn">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
